export type ToastType = 'success' | 'error' | 'info';

// 電話番号ログイン関連の型定義
export interface PhoneLoginRequest {
  phoneNumber: string;
}

export interface PhoneLoginResponse {
  success: boolean;
  message?: string;
  sessionId?: string;
}

export interface PhoneVerificationRequest {
  phoneNumber: string;
  verificationCode: string;
  sessionId: string;
}

export interface PhoneVerificationResponse {
  success: boolean;
  message?: string;
  isNewUser?: boolean;
  userId?: string;
  token?: string;
}

export interface PhoneUserRegistrationRequest {
  phoneNumber: string;
  nickname: string;
  sessionId: string;
}

export interface PhoneUserRegistrationResponse {
  success: boolean;
  message?: string;
  userId: string;
  token: string;
}

export interface PhoneUser {
  id: string;
  phoneNumber: string;
  nickname: string;
  isAdmin?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface User {
  id: string;
  name: string;
  isAdmin?: boolean; // Added for admin flag
  personalSettings?: PersonalUserSettings; // Added
  loginType?: 'mygarage' | 'phone' | 'aws_sms'; // Added for login type tracking
  phoneNumber?: string; // Added for phone login users
}

export interface CreditsRequestParams {
  credits: number;
}

export interface CreditsFetchParams {
  credits: number;
}

export interface CreditsOperationResponseParams {
  success: boolean;
  message?: string;
  remainingBalance?: number;
  newCredits?: number; // 後方互換性のため残す
}

export enum AspectRatio {
  Original = "Original",
  Square_1_1 = "1:1",
  Landscape_16_9 = "16:9",
  Portrait_9_16 = "9:16",
  Landscape_4_3 = "4:3",
  Portrait_3_4 = "3:4",
}

export interface PromptVariableInExecution {
  key: string;
  value: string;
}

// MenuExecutionPanel上の情報
export interface MenuExecutionFormData {
  category: AdminGenerationMenuCategoryItem | null;
  menu: AdminGenerationMenuItem | null;
  image: File | null;
  additionalPromptForMyCar?: string;
  additionalPromptForOthers?: string;
  aspectRatio?: AspectRatio;
  promptVariables: PromptVariableInExecution[];
  inputType: 'upload' | 'prompt';
  // FLUX.2用: 編集画像
  editImage?: File | null;
  // 動画i2v用: エンドフレーム画像
  endImage?: File | null;
  // HeyGen用: スクリプト・音声
  script?: string;
  audioFile?: File | null;
  // FLUX.2用: 参照画像の配列（ラベルのインデックスに対応）
  referenceImages?: (File | null)[];
  // 愛車ID（愛車テキスト参照用）
  carId?: string;
}

// MenuExecution API実行時のリクエストパラメータ群
export interface MenuExecutionRequestParams {
  menuId: number; // メニューIDはリクエストパラメータではなくURLに含める
  image?: File;
  additionalPromptForMyCar?: string;
  additionalPromptForOthers?: string;
  aspectRatio?: AspectRatio;
  promptVariables?: PromptVariableInExecution[];
  // FLUX.2用: 編集画像
  editImage?: File;
  // 動画i2v用: エンドフレーム画像
  endImage?: File;
  // HeyGen用
  script?: string;
  audioFile?: File | null;
  // FLUX.2用: 参照画像の配列
  referenceImages?: (File | null)[];
  // 愛車ID（愛車テキスト参照用）
  carId?: string;
}

export interface MenuExecutionResponseParams {
  generatedImageUrl: string;
  promptFormatted: string;
  createdAt: Date;
  frontendId?: string;
}

export interface GenerationOptions {
  selectedMenuId?: string; 
  userInputValues?: Record<string, string>; 

  finalPromptForService: string; 
  generationEngineForService: SupportedGenerationEngine; 
  creditCostForService: number;

  aspectRatio: AspectRatio;
  additionalInput?: string; 
  
  uploadedCarImageFile?: File | null; 
  uploadedCarImageDataUrl?: string | null; 
  originalUploadedImageDataUrl?: string | null; 
}

export interface GeneratedImage {
  id: string;
  url: string; 
  mediaType?: 'image' | 'video'; // APIが返す種別（urlの拡張子から判定）。動画は<video>で表示
  // sourceImageUrl?: string; 
  // originalUploadedImageDataUrl?: string; 
  displayPrompt: string; 
  menuName?: string;
  usedFormData: MenuExecutionFormData;
  fullOptions: GenerationOptions; // コメントアウトを解除
  timestamp: Date;
  rating?: 'good' | 'bad';
  isPublic: boolean; 
  authorName?: string;
  isSavedToLibrary?: boolean; // ライブラリに明示的に保存されたかどうか
  comment_count?: number; // コメント数（スネークケース）
  like_count?: number; // いいね数（スネークケース）
  commentCount?: number; // コメント数（キャメルケース）
  likeCount?: number; // いいね数（キャメルケース）
  goods_creation_count?: number; // グッズ作成回数
}

export interface GoodsVariation {
  id: string; 
  typeName: string; 
  options: string[]; 
}

export interface GoodsCreationRecord {
  id: string;
  goodsName: string;
  imageId: string; 
  imageUrl?: string; 
  prompt?: string; 
  timestamp: Date;
  creditCost: number;
  selectedVariations?: Record<string, string>;
  price?: number;
  display_price?: string;
  has_order?: boolean;   // 購入済フラグ（Prodigi等）
}

export interface Plan {
  id: number;
  name: string;
  price: number; 
  credits: number; 
}

export interface SuzuriItem { 
  id: string; 
  name: string; 
  apiId: string; 
  creditCost: number;
  variations?: GoodsVariation[];
  imageUrl?: string; 
}

export type SupportedGenerationEngine = 'imagen3' | 'ideogram' | 'midjourney' | 'black_forest_labs' | 'black_forest_labs2_pro' | 'black_forest_labs2_flex' | 'nanobanana' | 'nanobanana_pro' | 'nanobanana2' | 'grok_imagen' | 'seedream4' | 'seedream45' | 'seedream5' | 'gpt_image' | 'gpt_image2' | 'gpt_image25_sunburst' | 'gpt_image25_flare' | 'hotgen_image_v4_pro' | 'hotgen_image_v51_pro' | 'flux3_image' | 'grok_imagine2' | 'nanobanana2_lite' | 'seedance2' | 'seedance2_fast' | 'seedance25' | 'kling3_omni' | 'hotgen_video_v4_pro' | 'hotgen_video_v5' | 'seedance2_sousaku_refs' | 'seedance2_sousaku_i2v' | 'seedance25_sousaku_refs' | 'seedance25_sousaku_i2v' | 'minimax_h3_max_turbo' | 'runway_gen45' | 'gemini_omni_flash' | 'topview_t1_omni' | 'seedance25_topview_omni' | 'seedance2_topview_omni' | 'topview_t1_i2v' | 'seedance25_topview_i2v' | 'seedance2_topview_i2v' | 'heygen';

export interface AdminGenerationMenuCategoryItem {
  id: number;
  name: string;
  description?: string;
  orderIndex?: number;
}

export interface AdminGenerationMenuItem {
  id: number;
  name: string;
  categoryId?: number | null;
  description?: string;
  engine: SupportedGenerationEngine;
  prompt: string;
  negativePrompt?: string;
  promptVariables?: {
    label: string;
    key: string;
    multiline?: boolean;
  }[];
  credit: number;
  isHidden?: boolean; // 非表示フラグ（キャメルケース）
  is_hidden?: boolean; // 非表示フラグ（スネークケース）
  display_order?: number; // 表示順序
  sampleInputImageUrl?: string; // フロントエンド用（キャメルケース）
  sampleResultImageUrl?: string; // フロントエンド用（キャメルケース）
  sample_input_image_url?: string; // バックエンド用（スネークケース）
  sample_result_image_url?: string; // バックエンド用（スネークケース）
  usageGuideImageUrl?: string; // 使い方説明画像（キャメルケース）
  usage_guide_image_url?: string; // 使い方説明画像（スネークケース）
  goodsOrder?: string[]; // グッズ作成可能なサプライヤー（キャメルケース）
  goods_order?: string[]; // バックエンド用（スネークケース）
  // FLUX.2用: 編集画像・参照画像設定
  useEditImage?: boolean; // フロントエンド用（キャメルケース）
  use_edit_image?: boolean; // バックエンド用（スネークケース）
  refImageLabels?: string[]; // フロントエンド用（キャメルケース）
  ref_image_labels?: string[]; // バックエンド用（スネークケース）
  // メニュー固定参照画像
  menuReferenceImages?: { label: string; url: string }[]; // フロントエンド用（キャメルケース）
  menu_reference_images?: { label: string; url: string }[]; // バックエンド用（スネークケース）
  // 動画エンジン用: 人物参照ラベル（アセット登録対象、任意アップロード不可）
  videoPersonRefLabels?: string[]; // フロントエンド用（キャメルケース）
  video_person_ref_labels?: string[]; // バックエンド用（スネークケース）
  // 動画エンジン用: Renoise登録済みアセットの直接参照
  videoPersonAssets?: { asset_id: number; name: string; thumbnail_url?: string }[]; // キャメルケース
  video_person_assets?: { asset_id: number; name: string; thumbnail_url?: string }[]; // スネークケース
  // 動画エンジン用: 動画の秒数（4〜15）
  videoDurationSeconds?: number | null; // キャメルケース
  video_duration_seconds?: number | null; // スネークケース
  // 動画i2v型: エンドフレーム入力欄を表示するか（旧）
  videoUseEndFrame?: boolean; // キャメルケース
  video_use_end_frame?: boolean; // スネークケース
  // 動画用: ユーザー参照画像の表示ラベル
  videoUserImageLabel?: string;
  video_user_image_label?: string;
  // HeyGen設定
  heygen_voice_gender?: string;
  heygen_voice_id?: string;
  heygen_expressiveness?: string;
  heygen_motion_prompt?: string;
  heygen_resolution?: string;
  heygen_remove_background?: boolean;
  heygen_output_format?: string;
  heygen_default_script?: string;
  heygen_mode?: string;
  heygen_allow_user_script?: boolean;
  heygen_user_script_max_length?: number;
  heygen_character_avatar?: boolean;
  heygenVoiceGender?: string;
  heygenVoiceId?: string;
  heygenExpressiveness?: string;
  heygenMotionPrompt?: string;
  heygenResolution?: string;
  heygenRemoveBackground?: boolean;
  heygenOutputFormat?: string;
  heygenDefaultScript?: string;
  heygenMode?: string;
  heygenAllowUserScript?: boolean;
  heygenUserScriptMaxLength?: number;
  heygenCharacterAvatar?: boolean;
  // 動画i2v型: フレーム指定方法と固定画像URL
  videoStartFrameMode?: 'user' | 'fixed'; // キャメルケース
  video_start_frame_mode?: 'user' | 'fixed'; // スネークケース
  videoStartFrameUrl?: string;
  video_start_frame_url?: string;
  videoReferenceVideoUrl?: string; // TopView参照動画モード: メニュー固定の参照動画
  video_reference_video_url?: string;
  videoEndFrameMode?: 'none' | 'user' | 'fixed';
  video_end_frame_mode?: 'none' | 'user' | 'fixed';
  videoEndFrameUrl?: string;
  video_end_frame_url?: string;
  // デフォルトのアスペクト比
  defaultAspectRatio?: string | null; // フロントエンド用（キャメルケース）
  default_aspect_ratio?: string | null; // バックエンド用（スネークケース）
}

export interface AdminGoodsItem {
  id: string;
  suzuriApiId: string;
  goodsName: string;
  creditCost: number;
  variations?: GoodsVariation[];
  imageUrl?: string; 
}

export interface AdminChargeOptionItem {
  id: number;
  name: string; 
  priceYen: number;
  creditsAwarded: number;
  creditsBonus?: number; 
  displayInfo: string; 
  isActive: boolean;
}

export type AdminSection =
  | 'genMenuCategories'
  | 'genMenus'
  | 'chargeManagement'
  | 'engines'
  | 'generationHistory'
  | 'sales'
  | 'orderManagement'
  | 'users'
  | 'creditManagement'
  | 'goods'
  | 'suzuriApi'
  | 'stripeApi'
  | 'information'
  | 'apiKeyManagement';

export interface ExternalAPIKey {
  id: number;
  keyPrefix: string;
  name: string;
  userId: string | null;
  isActive: boolean;
  rateLimitPerMinute: number;
  rateLimitPerDay: number;
  totalRequests: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExternalAPIKeyRequest {
  name: string;
  userId?: string;
  rateLimitPerMinute?: number;
  rateLimitPerDay?: number;
}

export interface CreateExternalAPIKeyResponse {
  success: boolean;
  apiKey: string;
  keyInfo: ExternalAPIKey;
}

export interface SharePageParams {
  sharedByUser: string;
  sharedDate: string;
  sharedImageUrl: string;
  sharedPrompt?: string;
  sharedMenuName?: string;
}

export type ActionAfterLoadType = 
  | 'extend' 
  | { 
      type: 'loadFromLibrary'; 
      formData: MenuExecutionFormData; 
      generatedImageUrl?: string; 
    } 
  | null;
export type AppViewMode = 'generator' | 'timeline'; 

// --- START: Personal Settings Types ---
export interface NumberManagementSettings {
  licensePlateText?: string;
  logoMarkImageUrl?: string;      // Data URL for the image
  originalNumberImageUrl?: string; // Data URL for the image
}

export type CarPhotoAngle = 'front' | 'side' | 'rear' | 'front_angled_7_3' | 'rear_angled_7_3';

// MyGarage API から取得する愛車情報
export interface CarInfo {
  car_id: string;
  car_brand_n: string;  // 日本語ブランド名
  car_brand_en: string; // 英語ブランド名
  car_model_n: string;  // 日本語モデル名
  car_model_en: string; // 英語モデル名
  car_period_s: string; // 開始期間
  car_period_e: string; // 終了期間
  car_nickname: string | null; // ニックネーム
  car_image_url: string; // 車両画像URL
}

export interface CarReferencePhoto {
  viewAngle: CarPhotoAngle;
  label: string; // User-facing label e.g., "フロント正面"
  imageUrl?: string; // Data URL for the image
}

export interface ReferenceRegistrationSettings {
  favoriteCarName?: string;
  carPhotos: CarReferencePhoto[]; // Should always contain 5 items, one for each angle
}

export interface PersonalUserSettings {
  numberManagement: NumberManagementSettings;
  referenceRegistration: ReferenceRegistrationSettings;
}
// --- END: Personal Settings Types ---

// --- START: Car Settings API Types ---
export interface CarSettings {
  id: number;
  user_id: string;
  car_id: string;
  license_plate_text?: string | null;
  logo_mark_image_url?: string | null;
  original_number_image_url?: string | null;
  car_name?: string | null;
  car_photo_front_url?: string | null;
  car_photo_side_url?: string | null;
  car_photo_rear_url?: string | null;
  car_photo_diagonal_url?: string | null;
  car_photo_rear_diagonal_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CarSettingsCreateUpdateRequest {
  user_id: string;
  car_id: string;
  license_plate_text?: string;
  logo_mark_image?: File;
  original_number_image?: File;
  car_name?: string;
  car_photo_front?: File;
  car_photo_side?: File;
  car_photo_rear?: File;
  car_photo_diagonal?: File;
  car_photo_rear_diagonal?: File;
  // 削除フラグ
  delete_logo_mark_image?: boolean;
  delete_original_number_image?: boolean;
  delete_car_photo_front?: boolean;
  delete_car_photo_side?: boolean;
  delete_car_photo_rear?: boolean;
  delete_car_photo_diagonal?: boolean;
  delete_car_photo_rear_diagonal?: boolean;
}

export interface CarSettingsApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: any;
}
// --- END: Car Settings API Types ---


export class RateLimitError extends Error {
  title: string;
  details: string;
  constructor(title: string, details: string) {
    super(details); 
    this.name = "RateLimitError";
    this.title = title;
    this.details = details;
    Object.setPrototypeOf(this, RateLimitError.prototype);
  }
}

// コメント関連の型定義
export interface Comment {
  id: string;
  library_id?: string;
  user_id: string | null;
  user_name: string;
  content: string;
  created_at: string;
  updated_at: string;
  is_guest?: boolean;
}

// 参照設定関連の型定義
export type SelfPhotoType = 'front' | 'front_smile' | 'diagonal';
export type ReferenceCategory = 'person' | 'car' | 'animal' | 'object' | 'background' | 'costume';
export type StaffStyle = 'real' | 'anime' | 'illustration' | '3d';
export type CarModelStyle = 'real' | 'anime' | 'illustration' | '3d';
export type SpotCategory = 'scenic' | 'urban' | 'mountain' | 'seaside' | 'circuit' | 'parking' | 'other';

export interface SelfPhotoTypeOption {
  value: SelfPhotoType;
  label: string;
}

export interface ReferenceCategoryOption {
  value: ReferenceCategory;
  label: string;
}

export interface StaffStyleOption {
  value: StaffStyle;
  label: string;
}

export interface CarModelStyleOption {
  value: CarModelStyle;
  label: string;
}

export interface SpotCategoryOption {
  value: SpotCategory;
  label: string;
}

export interface SelfReference {
  id: number;
  user_id: string;
  reference_key: string;
  photo_type: SelfPhotoType;
  photo_type_display: string;
  photo_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface FamilyReference {
  id: number;
  user_id: string;
  reference_key: string;
  family_name: string;
  photo_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface GarageReference {
  id: number;
  user_id: string;
  reference_key: string;
  garage_name: string;
  photo_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface StaffReference {
  id: number;
  user_id: string;
  reference_key: string;
  staff_name: string;
  style: StaffStyle;
  style_display: string;
  photo_url: string | null;
  display_image_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface OtherReference {
  id: number;
  user_id: string;
  reference_key: string;
  reference_name: string;
  category: ReferenceCategory;
  category_display: string;
  photo_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CarModelReference {
  id: number;
  user_id: string;
  reference_key: string;
  model_name: string;
  style: CarModelStyle;
  style_display: string;
  photo_url: string | null;
  display_image_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CarSpotReference {
  id: number;
  user_id: string;
  reference_key: string;
  spot_name: string;
  category: SpotCategory;
  category_display: string;
  photo_url: string | null;
  display_image_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface AllReferencesResponse {
  self_references: SelfReference[];
  family_references: FamilyReference[];
  garage_references: GarageReference[];
  staff_references: StaffReference[];
  car_model_references: CarModelReference[];
  car_spot_references: CarSpotReference[];
  other_references: OtherReference[];
  self_photo_types: SelfPhotoTypeOption[];
  reference_categories: ReferenceCategoryOption[];
  staff_styles: StaffStyleOption[];
  car_model_styles: CarModelStyleOption[];
  spot_categories: SpotCategoryOption[];
}
